import { useEffect } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { tg } from "@/lib/telegram";
import { AuraBackground } from "@/components/AuraBackground";
import { useTopInset } from "@/lib/useTopInset";

/**
 * Главный layout: подключает BackButton от Telegram на не-главных страницах.
 * Когда юзер не на /, показываем стрелку «назад» Telegram-нативную, и она
 * возвращает в historyStack.
 */
export function Layout() {
  const location = useLocation();
  const navigate = useNavigate();

  // Safe-area для fullscreen-режиму (Bot API 8.0+). Telegram малює overlay-
  // кнопки (Закрити / Профіль / ···) які перекривають верх контенту —
  // компенсуємо paddingTop. Логіка — у спільному useTopInset (той самий
  // хук використовує Promo та інші standalone-сторінки → однаковий відступ).
  // Юзер 2026-05-22: «кнопки Telegram перекривають наш текст».
  const topInset = useTopInset();

  // ScrollToTop — при кожній зміні route скролимо вгору. Інакше Mini App
  // запам'ятовує scroll-позицію між сторінками: відкрив Home з прокруткою →
  // тицяєш «Профіль» → Profile рендериться з тим самим offset, виглядає
  // як «лагнуто». window.scrollTo + document.documentElement покриває
  // обидва способи скролу.
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [location.pathname]);

  // Ховаємо нативну Telegram-кнопку «Back» на ВСІХ сторінках — у застосунку
  // своя навігація (Меню з домиком / Назад зі стрілкою). Нативна зверху
  // зайва і дублює (юзер 2026-06-12: «спробуй BackButton.hide()»).
  useEffect(() => {
    const webapp = tg();
    if (!webapp?.BackButton) return;
    webapp.BackButton.hide();
  }, [location.pathname]);

  // ===========================================================
  // WIDTH CONTRACT (юзер 2026-05-22 «захардкоть текущую ширину
  // мини-апки, чтобы все будущие разделы делались по одному
  // шаблону в плане ширины»):
  //
  // • Контейнер: `mx-auto max-w-md` (Tailwind = 28rem = 448px)
  // • Горизонтальний padding: `px-5` (20px з кожного боку)
  // • Внутрішня ширина контенту: max-w-md MINUS 40px = ~408px
  //
  // ВСІ нові сторінки повинні:
  //   1. Або БУТИ В edge-to-edge списку нижче і додавати ВЛАСНИЙ
  //      `px-5` (для page-specific hero/sections),
  //   2. Або НЕ бути в списку — тоді Layout сам додасть `px-5 pt-5`
  //      і сторінка не повинна додавати свій horizontal padding.
  //
  // ❌ НЕ робіть px-7 / px-6 / px-4 — це створює візуально різну
  // ширину розділів. Юзер це бачить як «не той же розділ».
  // ===========================================================
  const path = location.pathname;
  const isEdgeToEdge =
    path === "/" ||
    path === "/profile" ||
    path === "/quests" ||
    path === "/feed" ||
    path.startsWith("/feed/") ||
    path === "/subscription";
  // Warm aura — вiдокремлений portal-style div ПЕРЕД основним wrapper'ом.
  // Так aura має власний z-index в кореневому stacking context (на рівні
  // body), а не всередині relative parent (де всі діти діляться однією
  // площиною). Тепер aura гарантовано ПІД усіма елементами Mini App.
  return (
    <>
      {/* Aura background — single source of truth у AuraBackground.tsx,
          щоб OnboardingGate і Onboarding flow (поза Layout) теж могли
          переиспользовать без копіпасти. */}
      <AuraBackground />
      <div
        className={
          "relative isolate " +
          (isEdgeToEdge
            ? "mx-auto min-h-screen max-w-md overflow-x-hidden"
            : "mx-auto min-h-screen max-w-md px-5")
        }
        style={{
          zIndex: 1,
          // padding-top = topInset (для fullscreen overlay-кнопок Telegram)
          // + 20 для non-edge-to-edge сторінок (дефолтний відступ що раніше
          // давав pt-5 у класі — прибрали з класу щоб inline style працював).
          paddingTop: `${topInset + (isEdgeToEdge ? 0 : 20)}px`,
        }}
      >
      {/* key={path} re-mount → CSS page-enter анімація прокручується з нуля. */}
      <div key={path} className="relative page-transition">
        <Outlet />
      </div>

      {/* Footer — clickable договір оферти. Глобальний для всіх сторінок
          крім /onboarding ТА самої /offerta (там була б ссилка сама на себе —
          юзер 2026-06-11). mt-24 → стабільно 96px між контентом і footer'ом. */}
      {path !== "/onboarding" && path !== "/offerta" && (
        <div className="relative mt-24 px-5 pb-10 text-center">
          <a
            href="/offerta"
            onClick={(e) => {
              e.preventDefault();
              navigate("/offerta");
            }}
            className="font-mono text-[0.6875rem] uppercase tracking-[0.12em] text-ink-dim active:text-ink underline underline-offset-4 decoration-ink-faint"
          >
            Договір оферти
          </a>
        </div>
      )}
      </div>
    </>
  );
}
